import commonTransformer from "./commonTransformer"
import { convertQueryObjToString, localisedValue } from "utils"
import inventoryDetailTransformer from "./inventoryDetailTransformer"
import inventoryFormDetailTransformer from "./inventoryFormDetailTransformer"
import { inventoryListingTransformer } from "./inventoryListingTransformer"
import inventoryProjectTransformer from "./inventoryProjectTransformer"
import listingLogsTransformer from "./listingLogsTransformer"
import parentApi from "store/parentApi"
import projectTypeLocationTransformer from "./projectTypeLocationTransformer"
import featureLogsTransformer from "./featureLogsTransformer"
import hierarchicalUnitLocationTransformer from "./hierarchicalUnitLocationTransformer.js"
import { dateTimeFunction } from "utils/date"
import { unitsDropDownTransformer } from "./unitsDropDownTransformer"
import { transformMultipleImage } from "../components/propertyDetailForm/commonUtilFunctions"

const inventoryApis = parentApi.injectEndpoints({
  endpoints: build => ({
    getListingInventory: build.query({
      query: ({ areaRange, areaUnitKey, priceRange, ...params }) => {
        let obj = { ...params }
        if (areaRange) {
          let area = areaRange.split(",")
          obj["landAreaFrom"] = area[0]
          obj["landAreaTo"] = area[1]
          obj["areaUnit"] = areaUnitKey
        }
        if (priceRange) {
          let price = priceRange.split(",")
          obj["minPrice"] = price[0]
          obj["maxPrice"] = price[1]
        }
        const checkDate = key => {
          if (!!obj[key]) {
            const dateR = obj[key].split(",")
            obj[key] = encodeURIComponent(`{"from":"${dateR[0]}","to":"${dateR[1]}"}`)
          }
        }

        checkDate("contract_date")
        checkDate("contract_expiry")
        delete obj["forUnitsDropDown"]
        return `/listings/searchPrimaryInventory?withDetail=true&listingAgainst=project&${convertQueryObjToString({
          ...obj,
        })}`
      },
      transformResponse: (response, meta, arg) =>
        arg?.forUnitsDropDown ? unitsDropDownTransformer(response) : inventoryListingTransformer(response),
      providesTags: ["inventory-listing"],
    }),

    getUnitNumber: build.query({
      query: obj => {
        return `/listings/getUnits?listingAgainst=project&${convertQueryObjToString({ ...obj })}`
      },
      transformResponse: res =>
        res.map(e => ({
          ...e,
          id: e.id,
          label: localisedValue({ object: e, value: "unitNumber" }),
          value: e.id.toString(),
        })),
    }),

    getUnitLocation: build.query({
      query: obj => {
        return `/projectSections/project/${obj.projectIds}`
      },
      transformResponse: res => hierarchicalUnitLocationTransformer(res),
    }),

    getInventoryProject: build.query({
      query: obj => `/projects/privilegedProjects?${convertQueryObjToString({ ...obj })}`,
      transformResponse: res => inventoryProjectTransformer(res),
    }),

    getListingsStatus: build.query({
      query: () => `/listingStatuses`,
      transformResponse: res =>
        res.map(e => ({ ...e, id: e.id, label: localisedValue({ object: e, key: "name" }), value: e.id.toString() })),
    }),

    getListingAllocationType: build.query({
      query: () => `/listingAllocationTypes`,
      transformResponse: res =>
        res.map(e => ({ ...e, value: e.key, label: localisedValue({ object: e, key: "name" }) })),
    }),

    getListingVisibilityTypes: build.query({
      query: () => `/listingVisibilityTypes`,
    }),

    getFeatureList: build.query({
      query: obj => `/features?${convertQueryObjToString({ ...obj })}`,
    }),

    getProjectSections: build.query({
      query: obj => `/projectSections?${convertQueryObjToString({ ...obj })}`,
      transformResponse: res =>
        res.rows.map(e => ({
          ...e,
          id: e.id.toString(),
          label: localisedValue({ object: e, key: "breadcrumbTitle" }),
        })),
    }),

    getProjectSectionsWithParent: build.query({
      query: obj => `/projectSections?${convertQueryObjToString({ ...obj })}`,
      transformResponse: res =>
        res.rows.map(e => ({
          ...e,
          id: e.id.toString(),
          label: localisedValue({ object: e, key: "breadcrumbTitle" }),
        })),
    }),

    getExportInventory: build.query({
      query: obj => `/listings/export?${convertQueryObjToString({ ...obj })}`,
    }),

    getInventoryDetail: build.query({
      query: id => `listings/${id}`,
      transformResponse: response => inventoryDetailTransformer(response),
    }),

    getListingLogs: build.query({
      query: id => `ListingsLogs/${id}`,
      transformResponse: response => listingLogsTransformer(response),
    }),

    getInventoryNotes: build.query({
      query: id => `/notes/inventory/${id}`,
      providesTags: ["inventoryNotes"],
    }),

    getFeatureLogs: build.query({
      query: params => `/logs?${convertQueryObjToString(params)}`,
      transformResponse: response => featureLogsTransformer(response),
    }),

    deleteNote: build.mutation({
      query: id => ({ url: `/notes/inventory/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, args) => (!error ? ["inventoryNotes", "inventory-listing"] : []),
    }),

    addNote: build.mutation({
      query: ({ ...values }) => {
        const payload = { note: values.note, objectId: values?.objectId, objectType: "inventory" }
        return { url: "/notes/inventory", method: "POST", body: payload }
      },
    }),

    changeState: build.mutation({
      query: ({ ...values }) => {
        const payload = { allocation: values.allocation, ids: values?.selectedIds }
        return { url: "/listings/changeStatus", method: "PUT", body: payload }
      },
      invalidatesTags: (_result, error, _args) => {
        return !error ? ["inventory-listing"] : []
      },
    }),
    updateListing: build.mutation({
      query: ({ ...values }) => {
        const payload = {
          ids: values?.selectedIds,
          areaUnitId: values.areaUnit,
          basePrice: values.basePrice,
          bath: values.bath,
          bed: values.bed,
          comments: values.comment,
          grossArea: values?.convertedArea?.grossArea,
          landArea: values.convertedArea?.landArea,
          listingAgainstId: values.project,
          maxDiscount: values.discount,
          note: values.note,
          price: values.price,
          purposeId: values.purpose,
          status: values.status,
          typeId: values.type,
          visibility: values.openTo,
          locationSectionId: values?.subSection ? values?.subSection : values?.section,
          FeatureCharges: values?.feature,
        }
        return { url: "/listings", method: "PUT", body: payload }
      },
      invalidatesTags: (_result, error, _args) => {
        return !error ? ["inventory-listing"] : []
      },
    }),
    getInventoryFormDetail: build.query({
      query: id => `/listings/${id}`,
      transformResponse: data => inventoryFormDetailTransformer(data),
    }),
    validateSellerId: build.query({
      query: id => `/internalInquiries/find/${id}?leadSide=supply&leadInterest=secondary`,
    }),

    getProjectAmenities: build.query({
      query: () => `/amenities`,
      transformResponse: data => commonTransformer(data),
    }),
    getInventoryCountry: build.query({
      query: () => `/categories/getArea?country=1&pinnedCountries=false`,
      transformResponse: data => commonTransformer(data),
    }),
    //TODO code replication needs to change
    getInventoryState: build.query({
      query: id => `/categories/getArea?id=${id}`,
      transformResponse: data => commonTransformer(data),
    }),
    getInventoryCity: build.query({
      query: id => `/categories/getArea?id=${id}`,
      transformResponse: data => commonTransformer(data),
    }),
    getInventoryLocality: build.query({
      query: id => `/categories/getArea?id=${id}`,
      transformResponse: data => commonTransformer(data),
    }),
    getProjectFeatures: build.query({
      query: id => `/features?projectId=${id}`,
      transformResponse: data => commonTransformer(data),
    }),

    getProjectTypeInventoryLocations: build.query({
      query: id => `/projectSections/project/${id}`,
      transformResponse: data => projectTypeLocationTransformer(data),
    }),
    getListingTag: build.query({
      query: () => `/listingTag`,
      transformResponse: data =>
        data?.map(item => ({ ...item, label: "Yes", value: item?.id })).concat([{ label: "No", value: 0 }]),
    }),

    bulkImportInventory: build.mutation({
      query: payload => {
        let body = {
          projectId: payload?.project,
          s3FileKey: payload?.file?.fileList?.[0]?.response.key,
        }
        return {
          url: "/listings/bulkImportInventory",
          method: "POST",
          body: body,
        }
      },
      invalidatesTags: ["inventory-listing"],
    }),
    importInventoryFeatures: build.mutation({
      query: payload => {
        let body = {
          projectId: parseInt(payload?.project),
          s3FileKey: payload?.file?.fileList?.[0]?.response.key,
          name: payload?.file?.file?.name,
        }
        return {
          url: "/listings/importListingFeatures",
          method: "POST",
          body: body,
        }
      },
      invalidatesTags: ["inventory-listing"],
    }),
    addUpdateInventory: build.mutation({
      query: ({ inventory, detail, setting, info, id }) => {
        let inventoryFloorPlan = detail?.inventoryFloorPlans?.list ? { ...detail?.inventoryFloorPlans?.list } : []

        Object.keys(detail?.inventoryFloorPlans?.list || {})?.forEach(item => {
          if (detail?.inventoryFloorPlans?.list?.[item]?.[0]?.src) {
            transformMultipleImage(inventoryFloorPlan)
          }
          inventoryFloorPlan = inventoryFloorPlan?.[item]
        })

        let body = {
          ...(id && { id: id }),
          listingAgainst: inventory?.inventoryType,
          listingAgainstId: +inventory?.project || inventory?.sellerLeadId,
          purposeId: +inventory?.purpose,
          basePrice: { value: inventory?.basePrice },
          price: { value: inventory?.publishedPrice },
          currencyId: inventory?.publishedPriceCurrency?.value,
          commissionValue: inventory?.commissionValue || null,
          commissionType: inventory?.commissionType,
          mouStatus: inventory?.mouStatus,
          mouStartDate: dateTimeFunction({ date: inventory?.mouDate?.[0], timeZone: true, apiRequest: true }),
          mouEndDate: dateTimeFunction({ date: inventory?.mouDate?.[1], timeZone: true, apiRequest: true }),
          discountedBasePrices: [],
          mouAttachments: inventory?.attachment?.fileList
            ?.map(item => ({
              s3Key: item?.response?.key || item?.s3Key,
              name: item?.name || item?.originFileObj?.name || item?.fileName,
              fileExtension: item?.type || item?.fileExtension,
              ...(item?.id && { id: item?.id }), 
            })),
          typeId: +detail?.type,
          locationSectionId: +detail?.location,
          features: detail?.featureCharges?.map(item => ({
            amount: item?.amount == "-" ? 0 : item?.amount,
            amountType: item?.type?.value || "none",
            featureId: item?.feature?.value,
            fixed: item?.fixed ? 1 : 0,
          })),
          amenities: detail?.amenities,
          grossArea: +detail?.grossArea / +detail?.areaUnit?.sqfConversion,
          landArea: +detail?.landArea / +detail?.areaUnit?.sqfConversion,
          areaUnitId: +detail?.areaUnit?.id,
          bed: detail?.beds,
          bath: detail?.bath,
          comments: setting?.statusComment || "",
          photos: detail?.photos
            ? detail?.photos?.fileList
                ?.filter(item => !item?.id)
                ?.map(item => ({
                  key: item?.response?.key,
                  fileName: item?.name,
                  fileExtension: item?.type,
                }))
            : [],
          documents: detail?.documents
            ? detail?.documents?.fileList
                ?.filter(item => !item?.id)
                ?.map(item => ({
                  key: item?.response?.key,
                  fileName: item?.name,
                  fileExtension: item?.type,
                }))
            : [],
          floorPlans: detail?.floorPlans
            ? detail?.floorPlans?.fileList
                ?.filter(item => !item?.id)
                ?.map(item => ({
                  key: item?.response?.key,
                  fileName: item?.name,
                  fileExtension: item?.type,
                }))
            : [],
          ...(inventory?.project && +detail?.location && { floorPlan: inventoryFloorPlan }),
          address: {
            countryId: detail?.country || 0,
            stateId: detail?.state?.value || 0,
            localityId: detail?.locality?.value || 0,
            cityId: detail?.city?.value || 0,
            city: detail?.city?.label || "",
            state: detail?.state?.label || "",
            locality: detail?.locality?.label || "",
            phase: detail?.phase || "",
            unitNumber: detail?.unit,
          },
          ...(inventory?.inventoryType == "lead" && { externalSystemId: inventory?.externalId }),
          unitNumber: detail?.unit,
          tagId: +setting?.label ? +setting?.label : null,
          status: +setting?.currentStatus,
          visibility: +setting?.openTo,
          allocation: +setting?.currentState,
          floorNumber: info?.floorNumber,
          petRequirement: info?.pets,
          occupancy: info?.occupancy,
          ...(info?.occupantDate && {
            vacantAt: dateTimeFunction({ date: info?.occupantDate, timeZone: true, apiRequest: true }),
          }),
          ...(info?.constructionDate && {
            constructionCompletionDate: dateTimeFunction({
              date: info?.constructionDate,
              timeZone: true,
              apiRequest: true,
            }),
          }),
          constructionPhase: info?.construction,
          propertyOwnership: info?.propertyOwnership,
        }

        return { url: id ? `listings/${id}` : `/listings`, method: id ? "PUT" : "POST", body: body }
      },
      invalidatesTags: ["inventory-listing"],
    }),

    bulkUploadFloorPlan: build.mutation({
      query: (data) => {
        let body = {
          selectedUnitIds:data?.selectedIds,
          mediaType:data?.mediaType,
          floorPlan: {
            name:data?.floorPlanAttachment?.[0].name || data?.floorPlanAttachment?.[0]?.originFileObj.name ,
            s3Key: data?.floorPlanAttachment?.[0].response.key
        }
        }
        return { url: `/listings/bulkUploadFloorPlans`, method: "POST", body: body }
      },
      invalidatesTags: ["inventory-listing"],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetListingInventoryQuery,
  useLazyGetListingInventoryQuery,
  useGetInventoryProjectQuery,
  useLazyGetInventoryProjectQuery,
  useLazyGetUnitLocationQuery,
  useGetListingsStatusQuery,
  useLazyGetListingsStatusQuery,
  useLazyGetListingAllocationTypeQuery,
  useLazyGetListingVisibilityTypesQuery,
  useLazyGetFeatureListQuery,
  useLazyGetProjectSectionsQuery,
  useLazyGetProjectSectionsWithParentQuery,
  useLazyGetExportInventoryQuery,
  useAddNoteMutation,
  useChangeStateMutation,
  useUpdateListingMutation,
  useLazyGetInventoryDetailQuery,
  useLazyGetListingLogsQuery,
  useLazyGetInventoryNotesQuery,
  useLazyGetFeatureLogsQuery,
  useDeleteNoteMutation,
  useGetInventoryFormDetailQuery,
  useValidateSellerIdQuery,
  useLazyValidateSellerIdQuery,
  useGetProjectAmenitiesQuery,
  useGetInventoryCountryQuery,
  useLazyGetInventoryStateQuery,
  useLazyGetInventoryLocalityQuery,
  useLazyGetInventoryCityQuery,
  useLazyGetProjectFeaturesQuery,
  useLazyGetInventoryCountryQuery,
  useLazyGetProjectAmenitiesQuery,
  useLazyGetProjectTypeInventoryLocationsQuery,
  useGetListingTagQuery,
  useGetListingVisibilityTypesQuery,
  useGetListingAllocationTypeQuery,
  useAddUpdateInventoryMutation,
  useBulkUploadFloorPlanMutation,
  useBulkImportInventoryMutation,
  useImportInventoryFeaturesMutation,
  useGetProjectTypeInventoryLocationsQuery,
} = inventoryApis
